import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import {
  Paper,
  Button,
  Link,
  Container,
  Box,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import '../translations/translations';
import { ValidatedTextInput } from '../components/ValidatedTextInput';
import logo from '../static/images/logo.svg';
import microsoftLogo from '../static/images/microsoft-logo.svg';
import { authActions } from '../store/auth/actions';
import { EmailTextInput } from '../components/EmailTextInput';
import {
  entraLoginRequest,
  handleEntraRedirect,
  msalInstance,
} from '../auth/msalConfig';

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '5rem 3rem',
  },
  paper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    width: '20rem',
  },
  logoContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '2rem',
  },
  logo: {
    maxWidth: '17rem',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  alignRight: {
    textAlign: 'right',
  },
  alignCenter: {
    textAlign: 'center',
  },
  spinner: {
    width: '20px !important',
    height: '20px !important',
  },
  divider: {
    textAlign: 'center',
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
}));

const Login: React.FC = () => {
  const classes = useStyles();
  const [isInputFilled, setIsInputFilled] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [inputs, setInputs] = useState({
    username: '',
    password: '',
  });
  const { username, password } = inputs;
  const dispatch = useDispatch();
  const { t } = useTranslation(['translations']);
  const [isMounted, setIsMounted] = useState(true);

  useEffect(() => {
    return () => {
      setIsMounted(false);
    };
  }, []);

  const [entraInProgress, setEntraInProgress] = useState(false);

  useEffect(() => {
    handleEntraRedirect()
      .then(async (result) => {
        if (result?.idToken) {
          setEntraInProgress(true);
          await dispatch(authActions.loginWithEntra(result.idToken));
        }
      })
      .catch(() => undefined)
      .finally(() => setEntraInProgress(false));
  }, []);

  const handleEntraLogin = () => {
    setEntraInProgress(true);
    msalInstance
      .loginRedirect(entraLoginRequest)
      .catch(() => setEntraInProgress(false));
  };

  useEffect(() => {
    setIsInputFilled(
      inputs.password !== undefined && inputs.username !== undefined,
    );
  }, [inputs]);

  const handleChange = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoggingIn(true);
    await dispatch(authActions.login(username, password));
    if (isMounted) {
      setIsLoggingIn(false);
    }
  };

  return (
    <Container className={classes.root}>
      <Paper className={classes.paper}>
        <Box className={classes.logoContainer}>
          <img src={logo} alt="Logo" className={classes.logo} />
        </Box>
        <form onSubmit={(e) => handleSubmit(e)} className={classes.form}>
          <EmailTextInput
            type="email"
            required={true}
            label={t('login_page.email')}
            onChange={handleChange}
            inputName="username"
            autoFocus={true}
            margin="normal"
          />

          <ValidatedTextInput
            type="password"
            required={true}
            label={t('login_page.password')}
            onChange={handleChange}
            inputName="password"
            autoFocus={false}
            margin="normal"
          />
          <Link
            href="/forgot-password"
            variant="body1"
            role="forgotPassLink"
            className={classes.alignRight}
          >
            {t('login_page.forgot_password_link')}
          </Link>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            className={classes.submit}
            disabled={!isInputFilled || isLoggingIn}
            startIcon={
              isLoggingIn ? (
                <CircularProgress className={classes.spinner} />
              ) : (
                <></>
              )
            }
          >
            {t('login_page.sign_in')}
          </Button>
        </form>
        <Box className={classes.divider}>{t('login_page.or')}</Box>
        <Button
          variant="outlined"
          color="primary"
          size="large"
          onClick={handleEntraLogin}
          disabled={entraInProgress || isLoggingIn}
          startIcon={
            entraInProgress ? (
              <CircularProgress className={classes.spinner} />
            ) : (
              <img src={microsoftLogo} alt="" />
            )
          }
        >
          {t('login_page.sign_in_with_microsoft')}
        </Button>
      </Paper>
    </Container>
  );
};

export default Login;
